import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { Appointment } from '@src/components/UserPortal/UserPortal.types';
import { mountDevTools } from '@src/store/utils/mount-dev-tools';
import { createSessionStorage } from '@src/store/utils/serialize-dates';

type AppointmentsInformationState = {
	appointments: Appointment[];
	updatedAt?: string | null;
};

type AppointmentsInformationActions = {
	setAllAppointments: (appointments: Partial<Appointment>[]) => void;
	getAppointmentById: (appointmentId: string) => Appointment | undefined;
	getAppointmentByPatientId: (patientId: string) => Appointment[];
	updateAppointment: (appointment: Partial<Appointment> & { id: string }) => void;
	removeAppointment: (appointmentId: string) => void;
	reset: () => void;
};

const INITIAL_STATE: AppointmentsInformationState = { appointments: [] };

const useAppointmentsStore = create<AppointmentsInformationState & AppointmentsInformationActions>()(
	persist(
		(set) => ({
			...INITIAL_STATE,
			setAllAppointments: (appointments: Partial<Appointment>[]) => {
				// normalize & sort ascending by start time if present
				const normalized = (appointments as Appointment[])
					.filter((a) => !!a && !!a.id)
					.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
				set({ appointments: normalized, updatedAt: new Date().toISOString() });
			},
			getAppointmentById: (appointmentId: string) => {
				const state = useAppointmentsStore.getState();
				return state.appointments.find((appointment) => appointment.id === appointmentId);
			},
			getAppointmentByPatientId: (patientId: string) => {
				const state = useAppointmentsStore.getState();
				return state.appointments.filter((appointment) => appointment.patientId === patientId);
			},
			updateAppointment: (appointment) =>
				set((prev) => {
					const next = prev.appointments.map((appt) =>
						appt.id === appointment.id ? { ...appt, ...appointment } : appt
					);
					// re-sort in case start time or other ordering field changed
					const sorted = next.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
					return { appointments: sorted, updatedAt: new Date().toISOString() };
				}),
			removeAppointment: (appointmentId) =>
				set((prev) => {
					const next = prev.appointments.filter((appt) => appt.id !== appointmentId);
					return { appointments: next, updatedAt: new Date().toISOString() };
				}),
			reset: () => set(INITIAL_STATE),
		}),
		{
			name: 'book-an-appointment-appointments-information',
			storage: createSessionStorage(),
		}
	)
);

mountDevTools('useAppointmentsStore', useAppointmentsStore);

export default useAppointmentsStore;
